import { date } from 'quasar'
import { consumerRoot, imgixHost, isAdmin, isConsumer } from 'src/config'
import { COUNTRY_CODES } from 'src/i18n'
import Circuit, { timeSlotKey } from 'src/models/circuit/Circuit'
import ConfigurationAudit from 'src/models/configuration-audit/ConfigurationAudit'
import Generic from 'src/models/generic/Generic'
import InventoryStrategy from 'src/models/inventory-strategy/InventoryStrategy'
import Model, { encoders, executeMutation, GqlError, ID, Integer, IsoDate, IsoDateTime, JsonArray, JsonObject, LocalizedJsonObject, MODEL_LEVEL } from 'src/models/model'
import Movie from 'src/models/movie/Movie'
import Tax from 'src/models/order/Tax'
import { ORDER_ITEM_TYPES } from 'src/models/order-item/OrderItem'
import Page from 'src/models/page/Page'
import { BUTTON_TYPES, SHOWTIMES_BUTTON_TYPES, SITE_SELECTOR_DISPLAY_TYPES } from 'src/models/page/Section'
import PriceTier from 'src/models/price-tier/PriceTier'
import Report from 'src/models/report/Report'
import SiteSchedule from 'src/models/schedule/SiteSchedule'
import Showing from 'src/models/showing/Showing'
import SignageMediaItem from 'src/models/signage/SignageMediaItem'
import SiteStripeConfiguration from 'src/models/site/SiteStripeConfiguration'
import SiteLocationZone from 'src/models/site-location-zone/SiteLocationZone'
import { DAY_CUTOFF_HOUR_OFFSET } from 'src/models/transfer/Transfer'
import VoucherType from 'src/models/voucher/VoucherType'
import { createConsumerSitePathRegexp, deletePrefix, deleteSuffix, prependWithConsumerRoot } from 'src/router/utils'

const { addToDate } = date

export const DEFAULT_PREP_PACK_PRINTING_OPTIONS = Object.freeze({
  docket_group_by: 'delivery_and_due_time',
  group_printed_items: 'variation_and_modifiers',
  print_delivery_types: true,
  print_order_employee: true,
  print_order_station: true,
  print_categories: true,
  print_number_of_guests: true,
  print_quantity_suffix: false,
  order_id_font_size: 'setTextNormal',
  order_name_font_size: 'setTextNormal',
  date_font_size: 'setTextNormal',
  time_font_size: 'setTextNormal',
  delivery_destination_font_size: 'setTextQuadArea bold',
  due_time_font_size: 'setTextDoubleHeight',
  categories_font_size: 'setTextQuadArea bold',
  item_font_size: 'setTextDoubleHeight',
  item_modifiers_font_size: 'setTextDoubleHeight',
  notes_font_size: 'setTextDoubleWidth',
  docket_per_print: false,
  pack_on_delivery: false,
  print_void_slips: true
})

export const SHOWTIME_LIST_VIEW_POS_OPTIONS = {
  SHOWTIMES_BY_FILM: 'film',
  SHOWTIMES_BY_FILM_COMPACT: 'film-compact',
  SHOWTIMES_BY_CINEMA: 'cinema',
  SHOWTIMES_BY_SCHEDULE: 'schedule'
}

export const INVENTORY_STRATEGY_TYPES = {
  SITE: 'site',
  LOCATION: 'location'
}
INVENTORY_STRATEGY_TYPES.ALL = _.values(INVENTORY_STRATEGY_TYPES)

export const COUNTER_PICKUP_IDENTIFIER_OPTIONS = {
  USER_NAME: 'user-name',
  SEAT_NAME: 'seat-name',
  DELIVERY_NAME: 'delivery-name',
  CUSTOM: 'custom'
}
COUNTER_PICKUP_IDENTIFIER_OPTIONS.ALL = _.values(COUNTER_PICKUP_IDENTIFIER_OPTIONS)

export const CONSUMER_TIP_TYPE_OPTIONS = ['percent_or_amount', 'percent', 'amount']

export const DONATION_TIP_TYPES = {
  AMOUNT: 'amount',
  PERCENT: 'percent'
}

export const DISPLAY_ORDER_OPTIONS = {
  NAME: 'name'
}
DISPLAY_ORDER_OPTIONS.ALL = _.values(DISPLAY_ORDER_OPTIONS)

export const HEADER_TYPES = {
  DEFAULT: 'default',
  STANDARD: 'standard',
  STACK: 'stack'
}
HEADER_TYPES.ALL = _.values(HEADER_TYPES)

export const HEADER_HOVER_TYPES = {
  DEFAULT: 'default',
  COLOR: 'color-shift',
  BORDER: 'border-grow',
  UNDERLINE: 'underline-slide'
}
HEADER_HOVER_TYPES.ALL = _.values(HEADER_HOVER_TYPES)

export default class Site extends Model {
  static name = 'Site'

  static isConfiguration = true

  static versioned = true

  static level = MODEL_LEVEL.SITE

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Site]

  static define () {
    this.fields = {
      id: {
        type: ID,
        required: true
      },
      name: {
        type: String
      },
      abbreviation: {
        type: String,
        computed () {
          if (this.isRemote) return this.data.abbreviation

          return _.toUpper(this.name.replace(/\W/g, '').substring(0, 3), 3)
        }
      },
      siteDescription: {
        type: String,
        default: null
      },
      circuitId: {
        type: ID,
        output: true
      },
      circuit: {
        type: Circuit,
        output: true
      },
      siteStripeConfiguration: {
        type: SiteStripeConfiguration,
        acceptsNestedAttributes: true,
        allowDestroy: false,
        default: null
      },
      isLibrary: {
        type: Boolean,
        default: false,
        output: true
      },
      siteTitle: {
        type: String
      },
      siteIdentifier: {
        type: String
      },
      phone: {
        type: String
      },
      email: {
        type: String
      },

      address1: {
        type: String
      },
      city: {
        type: String
      },
      state: {
        type: String
      },
      zip: {
        type: String
      },
      lat: {
        type: Number,
        output: true
      },
      lon: {
        type: Number,
        output: true
      },
      facebook: {
        type: String
      },
      instagram: {
        type: String
      },
      twitter: {
        type: String
      },
      snapchat: {
        type: String
      },
      youtube: {
        type: String
      },
      tiktok: {
        type: String
      },
      displayOrder: {
        type: Integer,
        required: true,
        default: 100
      },
      showDonationOnRegister: {
        type: Boolean,
        default: false
      },
      donationTitle: {
        type: String,
        default: 'Take the time to donate?',
        translatable: true
      },
      donationDescription: {
        type: String,
        default: 'We rely on the generosity of our patrons to keep the doors open.\nThank you for all your support - whether you\'re buying tickets or adding on a little extra.',
        translatable: true
      },
      sendToDonorPerfect: {
        type: Boolean,
        default: false
      },
      donorPerfectCampaignCodeForCheckout: {
        type: String
      },
      donorPerfectCampaignTitleForCheckout: {
        type: String
      },
      sendingDomain: {
        type: String
      },

      adminFromEmail: {
        type: String
      },
      adminSendingDomain: {
        type: String
      },
      marketingFromEmail: {
        type: String
      },
      marketingSendingDomain: {
        type: String
      },
      transactionalFromEmail: {
        type: String
      },
      transactionalSendingDomain: {
        type: String
      },

      currentSiteSchedule: {
        type: SiteSchedule,
        reference: true,
        output: true,
        excludeFromDefault: true
      },
      modelAudits: {
        type: [ConfigurationAudit],
        default: () => { return [] },
        output: true,
        excludeFromDefault: !isAdmin
      },

      defaultPriceCards: {
        type: JsonObject,
        default: () => { return {} }
      },
      priceTier: {
        type: PriceTier,
        reference: true
      },

      allowAnonymousOrders: {
        type: Boolean,
        default: true
      },
      membershipRequired: {
        type: Boolean,
        default: false
      },
      allowMonthlyCashMembership: {
        type: Boolean,
        default: false
      },
      allowMultiMonthCashMembership: {
        type: Boolean,
        default: false
      },
      dayPassEnabled: {
        type: Boolean,
        default: false
      },
      dayPassVoucherType: {
        type: VoucherType,
        default: null,
        encode: encoders.hasOne,
        reference: true
      },
      dayPassPrice: {
        type: Number,
        encode: encoders.cents,
        default: 0
      },
      siteLocationZone: {
        type: SiteLocationZone,
        reference: true,
        encode: encoders.hasOne,
        default: null
      },
      webHomePage: {
        type: Page,
        default: null,
        encode: encoders.hasOne,
        reference: true
      },
      appHomePage: {
        type: Page,
        default: null,
        encode: encoders.hasOne,
        reference: true
      },
      iso31661: {
        type: String,
        default: null,
        options: _.values(COUNTRY_CODES)
      },
      dayPassTaxable: {
        type: Boolean,
        default: false
      },
      dayPassTaxes: {
        type: [Tax],
        default: () => ([]),
        reference: true
      },

      ticketsTaxable: {
        type: Boolean,
        default: false
      },
      ticketTaxes: {
        type: [Tax],
        default: () => ([]),
        reference: true
      },
      ticketPricesIncludeTax: {
        type: Boolean,
        default: true
      },

      hasMaxTicketsPerOrderPerShowing: {
        type: Boolean,
        default: false
      },
      maxTicketsPerOrderPerShowing: {
        type: Integer,
        default: 10
      },

      allowTicketAdmissionWithoutCollection: {
        type: Boolean,
        default: false
      },

      concessionPricesIncludeTax: {
        type: Boolean,
        default: true
      },

      roundPrices: {
        type: Boolean,
        default: true
      },

      allowCashPayments: {
        type: Boolean,
        default: true
      },
      allowTabOrders: {
        type: Boolean,
        default: false
      },
      requireCreditCardForTabOrders: {
        type: Boolean,
        default: true
      },
      autoPrintTabCard: {
        type: Boolean,
        default: false
      },
      collectSignature: {
        type: Boolean,
        default: false
      },

      counterPickupEnabled: {
        type: Boolean,
        default: false
      },
      hasCounterPager: {
        type: Boolean,
        default: false
      },
      useNameForCounterPickup: {
        type: Boolean,
        default: true
      },
      autoPrintClaimSlip: {
        type: Boolean,
        default: true
      },

      expressPickupEnabled: {
        type: Boolean,
        default: false
      },
      useNameForExpressPickup: {
        type: Boolean,
        default: true
      },

      theaterSeatDeliveryEnabled: {
        type: Boolean,
        default: false
      },
      theaterSeatTabShowingCutoverMinutes: {
        type: Integer,
        default: 15
      },
      theaterSeatDeliveryCutoffMinutes: {
        type: Integer,
        default: 0
      },
      tableDeliveryEnabled: {
        type: Boolean,
        default: false
      },
      tableNames: {
        type: JsonArray,
        default () {
          return []
        }
      },

      counterPickupAvailableOnline: {
        type: Boolean,
        default: false
      },
      expressPickupAvailableOnline: {
        type: Boolean,
        default: false
      },
      theaterSeatDeliveryAvailableOnline: {
        type: Boolean,
        default: false
      },
      tableDeliveryAvailableOnline: {
        type: Boolean,
        default: false
      },
      deliveryTypePriorityForRemoteOrders: {
        type: JsonArray,
        default: () => ([])
      },
      counterPickupIdentifierPriorityForRemoteOrders: {
        type: JsonArray,
        default: () => ([])
      },
      emailOnOnlinePickupOrderDelivery: {
        type: Boolean,
        default: false
      },
      smsOnOnlinePickupOrderDelivery: {
        type: Boolean,
        default: false
      },
      allowItemOrdersOnline: {
        type: Boolean,
        default: true,
        output: true
      },
      packTimeMinutes: {
        type: Integer,
        default: 5
      },
      coalesceCounterPickupOrders: {
        type: Boolean,
        default: false
      },
      coalesceTheaterSeatDeliveryOrders: {
        type: Boolean,
        default: false
      },
      coalesceTableDeliveryOrders: {
        type: Boolean,
        default: false
      },
      usePickPrepWaitAndFire: {
        type: Boolean,
        default: false
      },

      hasOnlineTicketing: {
        type: Boolean,
        default: false
      },
      hasReservedSeating: {
        type: Boolean,
        default: false
      },
      consumerShowReservedSeatLabels: {
        type: Boolean,
        default: false
      },
      posShowReservedSeatLabels: {
        type: Boolean,
        default: false
      },
      showCart: {
        type: Boolean,
        default: false
      },
      sendConcessionsReminders: {
        type: Boolean,
        default: false
      },
      signageConfig: {
        type: JsonObject,
        default: () => ({
          display_rating: false,
          threshold_minutes: 60,
          use_end_time: false
        })
      },
      printPackingSlipForSingleLocationOrders: {
        type: Boolean,
        default: false
      },
      autoSelectSeats: {
        type: Boolean,
        default: true,
        local: true
      },

      defaultDashboard: {
        type: Report,
        reference: true,
        encode: encoders.hasOne
      },

      enabledSignageMediaMovies: {
        type: [Movie],
        output: true,
        excludeFromDefault: true,
        default () {
          return []
        }
      },
      enabledSignageMediaItems: {
        type: [SignageMediaItem],
        output: true,
        excludeFromDefault: true,
        default () {
          return []
        }
      },
      todaysShowings: {
        type: [Showing],
        output: true
      },

      customHeaderContent: {
        type: String,
        excludeFromDefault: true
      },

      customBodyContent: {
        type: String,
        excludeFromDefault: true
      },

      styles: {
        type: String,
        default: '',
        excludeFromDefault: true
      },

      currentAuditLevels: {
        type: JsonArray,
        default () {
          return []
        },
        excludeFromDefault: true,
        output: true
      },

      currentDistanceText: {
        type: String,
        default: null,
        local: true
      },
      currentDistanceValue: {
        type: Number,
        default: null,
        local: true
      },

      transformedStyles: {
        type: String,
        excludeFromDefault: true,
        output: true
      },

      darkLogoImage: {
        type: String,
        default: 'g9ebtuvdqwp85moaggalmcjwrp5a'
      },
      darkLogoImageParams: {
        type: String,
        default: 'w=400&auto=format,compress',
        // output: true
        local: true // TODO
      },
      darkLogoImagePreviewParams: {
        type: String,
        default: 'w=400&transparency=grid&auto=format,compress',
        // output: true
        local: true // TODO
      },

      lightLogoImage: {
        type: String,
        default: 'g9ebtuvdqwp85moaggalmcjwrp5a'
      },
      lightLogoImageParams: {
        type: String,
        default: 'w=400&auto=format,compress',
        // output: true
        local: true // TODO
      },
      lightLogoImagePreviewParams: {
        type: String,
        default: 'w=400&transparency=grid&auto=format,compress',
        // output: true
        local: true // TODO
      },

      darkLogoMarkImage: {
        type: String,
        default: 'g9ebtuvdqwp85moaggalmcjwrp5a'
      },
      darkLogoMarkImageParams: {
        type: String,
        default: 'w=400&h=400&fit=fill&fill=solid&auto=format,compress',
        // output: true
        local: true // TODO
      },
      darkLogoMarkImagePreviewParams: {
        type: String,
        default: 'w=400&h=400&fit=fill&fill=solid&transparency=grid&auto=format,compress',
        // output: true
        local: true // TODO
      },

      lightLogoMarkImage: {
        type: String,
        default: 'g9ebtuvdqwp85moaggalmcjwrp5a'
      },
      lightLogoMarkImageParams: {
        type: String,
        default: 'w=400&h=400&fit=fill&fill=solid&auto=format,compress',
        // output: true
        local: true // TODO
      },
      lightLogoMarkImagePreviewParams: {
        type: String,
        default: 'w=400&h=400&fit=fill&fill=solid&transparency=grid&auto=format,compress',
        // output: true
        local: true // TODO
      },

      iconImage: {
        type: String,
        default: 'g9ebtuvdqwp85moaggalmcjwrp5a'
      },
      iconImageParams: {
        type: String,
        default: 'w=128&h=128&fit=fill&fill=solid&auto=format,compress',
        // output: true
        local: true // TODO
      },
      iconImagePreviewParams: {
        type: String,
        default: 'w=128&h=128&fit=fill&fill=solid&transparency=grid&auto=format,compress',
        // output: true
        local: true // TODO
      },

      faviconImage: {
        type: String,
        default: 'g9ebtuvdqwp85moaggalmcjwrp5a'
      },
      faviconImageParams: {
        type: String,
        default: 'w=32&h=32&fit=fill&fill=solid&auto=format,compress',
        // output: true
        local: true // TODO
      },
      faviconImagePreviewParams: {
        type: String,
        default: 'w=32&h=32&fit=fill&fill=solid&transparency=grid&auto=format,compress',
        // output: true
        local: true // TODO
      },

      splashImage: {
        type: String,
        default: 'g9ebtuvdqwp85moaggalmcjwrp5a'
      },
      splashImageParams: {
        type: String,
        default: 'w=1200&auto=format,compress',
        // output: true
        local: true // TODO
      },
      splashImagePreviewParams: {
        type: String,
        default: 'w=400&transparency=grid&auto=format,compress',
        // output: true
        local: true // TODO
      },

      appleStoreImage: {
        type: String,
        default: ''
      },
      appleStoreImageParams: {
        type: String,
        default: 'w=1024&h=1024&fit=fill&auto=format,compress',
        local: true
      },
      previewAppleStoreImageParams: {
        type: String,
        default: 'w=1024&h=1024&fit=fill&auto=format,compress',
        local: true
      },
      appleStoreName: {
        type: String
      },

      receiptLogoImage: {
        type: String,
        default: 'the3tnddsie4e4ggh1qvd6ygq7j8'
      },
      receiptLogoImageParams: {
        type: String,
        default: 'fit=clamp&w=576&bri=-100&sat=-100&png&auto=format,compress',
        // output: true
        local: true // TODO
      },
      previewReceiptLogoImageParams: {
        type: String,
        default: 'fit=clamp&w=576&bri=-100&sat=-100&png&auto=format,compress',
        // output: true
        local: true // TODO
      },

      rewardCurrencyImage: {
        type: String,
        default: ''
      },
      rewardCurrencyImageParams: {
        type: String,
        default: 'w=190&h=118&fit=clamp&auto=format,compress',
        local: true
      },
      previewRewardCurrencyImageParams: {
        type: String,
        default: 'w=190&h=118&fit=clamp&auto=format,compress',
        local: true
      },

      discountImage: {
        type: String,
        default: 'cf5x7d7yrmw5j3eziju85owdljgm'
      },
      registerButtonImageParams: {
        type: String,
        default: 'w=190&h=118&fit=crop&fill=solid&fill-color=5b5b5b&bg=5b5b5b&auto=format,compress',
        local: true
      },
      voucherSaleImage: {
        type: String,
        default: 'cf5x7d7yrmw5j3eziju85owdljgm'
      },
      voucherServiceIssueImage: {
        type: String,
        default: 'cf5x7d7yrmw5j3eziju85owdljgm'
      },
      giftCardImage: {
        type: String,
        default: 'cf5x7d7yrmw5j3eziju85owdljgm'
      },
      miscItemImage: {
        type: String,
        default: 'cf5x7d7yrmw5j3eziju85owdljgm'
      },
      cashOutItemImage: {
        type: String,
        default: 'cf5x7d7yrmw5j3eziju85owdljgm'
      },
      donationItemImage: {
        type: String,
        default: 'cf5x7d7yrmw5j3eziju85owdljgm'
      },
      userCreditImage: {
        type: String,
        default: 'cf5x7d7yrmw5j3eziju85owdljgm'
      },
      applyVoucherImage: {
        type: String,
        default: 'cf5x7d7yrmw5j3eziju85owdljgm'
      },

      movieNowPlayingIconName: {
        type: String,
        default: 'local_movies'
      },
      movieIconName: {
        type: String,
        default: 'movie'
      },
      movieNowPlayingText: {
        type: String,
        default: 'Now Playing',
        translatable: true
      },
      movieCurrentText: {
        type: String,
        default: 'Current Movies',
        translatable: true
      },
      movieIdentifierText: {
        type: String,
        default: 'Movies',
        translatable: true
      },

      consumerConfig: {
        type: JsonObject,
        mergeDefault: true,
        default () {
          return {
            darkMode: false,
            headerType: 'default',
            headerShowSocialIcons: false,
            headerLogoCentered: false,
            headerBgTransparent: false,
            headerFullWidth: false,
            stickyHeader: true,
            headerStackedText: false,
            headerButtonHoverStyle: 'default',
            showSiteSelectorOnNavigations: false,
            currentLocationDetailLinkUrl: null,
            showSiteSelectorOnMoviePage: false,
            hideCurrentLocation: false,
            siteSelectorDisplayType: SITE_SELECTOR_DISPLAY_TYPES.BUTTON,
            siteSelectorSiteIds: [],
            siteSelectorButtonType: BUTTON_TYPES.STANDARD,
            siteSelectorButtonColor: 'primary',
            siteSelectorButtonColorHex: '#ffffff',
            siteSelectorBlockBackgroundColor: 'primary',
            siteSelectorBlockBackgroundColorHex: '#ffffff',
            siteSelectorBlockTextColor: 'custom',
            siteSelectorBlockTextColorHex: '#ffffff',
            siteSelectorBorderRadius: 5,
            siteSelectorIncludeAddressDetail: false,
            siteSelectorCardColor: '',
            siteThumbnailImage: '',
            hideEmail: false,
            hidePhone: false,
            showSavedCard: true,
            collapseOtherTickets: false,
            hideOnLocationSelectPage: false,
            displayName: '',
            showDonation: false,
            consumerDefaultDonationAmounts: [1, 2, 5],
            consumerDefaultDonationPercents: [10, 15, 20, 25],
            consumerDonationTypes: [DONATION_TIP_TYPES.PERCENT, DONATION_TIP_TYPES.AMOUNT],
            donationPreselectedDefaultValue: false,
            allowItemSalesWithTicketPurchases: true,
            showTicketFirstCheckoutFlow: false,
            showBookingFeeInCart: false,
            enableSmsReceiptsForQrCodeOrders: false,
            showMoviesAndShowingsForMultipleSites: false
          }
        }
      },
      moviePageConfig: {
        type: JsonObject,
        mergeDefault: true,
        default () {
          return {
            hidePosters: false,
            backgroundColor: null,
            backgroundColorHex: null,
            useLightText: false,
            showButtonProgressBar: false,
            buttonType: SHOWTIMES_BUTTON_TYPES.BASIC,
            buttonBorderRadius: 3
          }
        }
      },
      bookingFeeText: {
        type: String,
        default: 'Booking Fee',
        translatable: true
      },
      ticketPrintingOptions: {
        type: JsonObject,
        mergeDefault: true,
        default () {
          return {
            print_logo_on_tickets: false,
            print_qr_code_on_tickets: true,
            print_address_on_tickets: false,
            include_booking_fee: false,
            custom_message_enabled: false,
            custom_message_title: null,
            custom_message_line_1: null,
            custom_message_line_2: null,
            custom_message_line_3: null,
            partial_cut_tickets: false
          }
        }
      },
      reportingOptions: {
        type: JsonObject,
        mergeDefault: true,
        default () {
          return {
            exclude_uncollected_free_tickets: false
          }
        }
      },
      giftCardPrintingOptions: {
        type: JsonObject,
        mergeDefault: true,
        default () {
          return {
            print_logo: false,
            print_qr_code: true
          }
        }
      },
      voucherPrintingOptions: {
        type: JsonObject,
        mergeDefault: true,
        default () {
          return {
            print_logo: false,
            print_qr_code: true
          }
        }
      },
      invoiceOptions: {
        type: JsonObject,
        default () {
          return {}
        }
      },
      tippingOptions: {
        type: LocalizedJsonObject,
        mergeDefault: true,
        default () {
          return {
            enable_consumer_qr_tips: false,
            consumer_widget_title: null,
            consumer_widget_description: null,
            consumer_default_percent: 0,
            consumer_default_amount: 0,
            consumer_tip_types: [DONATION_TIP_TYPES.PERCENT, DONATION_TIP_TYPES.AMOUNT],
            preselected_default_value: false
          }
        },
        translatables: ['consumer_widget_title', 'consumer_widget_description']
      },
      receiptPrintingOptions: {
        type: JsonObject,
        mergeDefault: true,
        default () {
          return {
            show_employee_name: false,
            show_register_name: false,
            include_movie_images: true,
            include_item_images: true,
            include_item_modifiers: true,
            thermal_receipt_print_tip_options: false,
            split_taxes: false,
            thermal_receipt_tip_message: 'Suggested Gratuity',
            thermal_receipt_open_tab_show_payments: false,
            thermal_receipt_tip_options: [18, 20, 22],
            thermal_receipt_partial_cut: false
          }
        }
      },
      prepPrintingOptions: {
        type: JsonObject,
        mergeDefault: true,
        default: () => ({ ...DEFAULT_PREP_PACK_PRINTING_OPTIONS }),
        excludeFromDefault: true
      },
      packPrintingOptions: {
        type: JsonObject,
        mergeDefault: true,
        default: () => ({ ...DEFAULT_PREP_PACK_PRINTING_OPTIONS }),
        excludeFromDefault: true
      },
      claimSlipPrintingOptions: {
        type: JsonObject,
        mergeDefault: true,
        default: () => ({ ...DEFAULT_PREP_PACK_PRINTING_OPTIONS }),
        excludeFromDefault: true
      },
      cashCountPrintingOptions: {
        type: JsonObject,
        mergeDefault: true,
        default () {
          return {
            print_credit_card_summary: false,
            print_tip_share_percentage: false,
            tip_share_percentage: 4,
            print_tip_by_payment_type: false
          }
        }
      },
      pnlConfig: {
        type: JsonObject,
        excludeFromDefault: true,
        default () {
          return {}
        }
      },
      useThemeForAdmin: {
        type: Boolean,
        default: true
      },
      useThemeForPos: {
        type: Boolean,
        default: true
      },
      defaultPosShowtimeViewPreference: {
        type: String,
        default: 'film',
        options: [
          { label: 'By Film', value: SHOWTIME_LIST_VIEW_POS_OPTIONS.SHOWTIMES_BY_FILM },
          { label: 'By Film Compact', value: SHOWTIME_LIST_VIEW_POS_OPTIONS.SHOWTIMES_BY_FILM_COMPACT },
          { label: 'By Cinema', value: SHOWTIME_LIST_VIEW_POS_OPTIONS.SHOWTIMES_BY_CINEMA },
          { label: 'By Schedule', value: SHOWTIME_LIST_VIEW_POS_OPTIONS.SHOWTIMES_BY_SCHEDULE }
        ]
      },
      logoRoute: {
        type: String,
        default: '/'
      },
      defaultExploreRoute: {
        type: String,
        default: '/movies'
      },

      primaryColor: {
        type: String,
        default: '#027be3',
        mergeDefault: true
      },
      secondaryColor: {
        type: String,
        default: '#26a69a',
        mergeDefault: true
      },
      accentColor: {
        type: String,
        default: '#9c27b0',
        mergeDefault: true
      },
      darkColor: {
        type: String,
        default: '#1d1d1d',
        mergeDefault: true
      },
      positiveColor: {
        type: String,
        default: '#21ba45',
        mergeDefault: true
      },
      negativeColor: {
        type: String,
        default: '#c10015',
        mergeDefault: true
      },
      infoColor: {
        type: String,
        default: '#31ccec',
        mergeDefault: true
      },
      warningColor: {
        type: String,
        default: '#f2c037',
        mergeDefault: true
      },
      backgroundColor: {
        type: String,
        default: '#FFFFFF'
      },
      splashBackgroundColor: {
        type: String,
        default: '#FFFFFF'
      },
      seatSelectedColor: {
        type: String,
        default: '#1976d2'
      },
      seatAvailableColor: {
        type: String,
        default: '#26a69a'
      },
      headerColor: {
        type: String,
        default: '#1976d2'
      },
      timeZone: {
        type: String
      },
      timeZoneOptions: {
        type: JsonArray,
        output: true,
        excludeFromDefault: true
      },

      stripeConnectionToken: {
        type: String,
        default: null,
        excludeFromDefault: true
      },
      showConsumerAttendanceBadge: {
        type: Boolean,
        default: false
      },
      showAttendancePredictionInPos: {
        type: Boolean,
        default: false
      },
      resetPosTicketPageDatesOnPurchase: {
        type: Boolean,
        default: false
      },

      showPastShowingsInPos: {
        type: Boolean,
        default: false
      },
      showPastShowingsOnline: {
        type: Boolean,
        default: false
      },
      showPastShowingsOnlineCutoffMinutes: {
        type: Integer,
        default: 120
      },
      showingCutoffOnlineMinutes: {
        type: Integer,
        default: 0
      },

      sendBors: {
        type: Boolean,
        default: false
      },
      simpleBorTaxReporting: {
        type: Boolean,
        default: true
      },
      sendRentrak: {
        type: Boolean,
        default: false
      },
      rentrakId: {
        type: String,
        default: null
      },
      rentrakUsername: {
        type: String,
        default: null
      },
      rentrakPassword: {
        type: String,
        default: null
      },
      sendNumero: {
        type: Boolean,
        default: false
      },
      sendDcm: {
        type: Boolean,
        default: false
      },
      dcmEdiSiteId: {
        type: String,
        default: null
      },
      sendMaccsbox: {
        type: Boolean,
        default: false
      },
      ceaCardEnabled: {
        type: Boolean,
        default: false
      },
      meerkatMovieEnabledValue: {
        type: Boolean,
        default: false
      },
      meerkatMovieCinemaEdicodeValue: {
        type: String,
        default: null
      },
      theCinemaClubEnabledValue: {
        type: Boolean,
        default: false
      },
      promptForPhoto: {
        type: Boolean,
        default: true
      },
      promptForMembershipCard: {
        type: Boolean,
        default: true
      },
      promptForGuestDetails: {
        type: Boolean,
        default: true
      },
      promptForCreditCard: {
        type: Boolean,
        default: true
      },
      checkoutOptions: {
        type: JsonObject,
        mergeDefault: true,
        default () {
          return {
            show_more_films_during_checkout: false,
            show_more_activities_during_checkout: false,
            allow_qr_code_delivery_location_pager_requests: false,
            allow_donation_honoree: false,
            donation_legal_name: null,
            send_tax_deductible_receipt: false
          }
        }
      },
      accountOptions: {
        type: LocalizedJsonObject,
        mergeDefault: true,
        default () {
          return {
            show_login_help_banner: false,
            login_help_banner_header_override: null,
            login_help_banner_message_override: null,
            login_help_banner_color: 'warning',
            login_help_banner_color_hex: '#f2c037',
            require_minimum_age: false,
            minimum_age: null, // null or integer
            check_age_on_registration: false
          }
        },
        translatables: ['login_help_banner_header_override', 'login_help_banner_message_override']
      },
      timeClockConfig: {
        type: JsonObject,
        mergeDefault: true,
        default () {
          return {
            time_clock_enabled: false,
            job_type_enabled: false
          }
        }
      },
      anonymousOrderRequirements: {
        type: JsonObject,
        mergeDefault: true,
        default: () => ({
          allow_phone_orders: true,
          phone_prompt_for_email: true,
          phone_email_required: false,
          phone_prompt_for_name: true,
          phone_name_required: true,
          consumer_prompt_for_name: false,
          consumer_name_required: false,
          phone_prompt_for_phone: false,
          phone_phone_required: false,
          consumer_prompt_for_phone: false,
          consumer_phone_required: false,
          phone_prompt_for_comments: false
        })
      },
      allowInvoiceOrders: {
        type: Boolean,
        default: false
      },

      allowConsumerRefunds: {
        type: Boolean,
        default: false
      },
      allowConsumerCreditCardRefunds: {
        type: Boolean,
        default: false
      },
      consumerRefundCutoffMinutes: {
        type: Integer,
        default: 30
      },

      showTaxablePaymentOptionInPos: {
        type: Boolean,
        default: true
      },
      setRegisterStationAllowedEmployee: {
        type: Boolean,
        default: true
      },
      collapseOtherTickets: {
        type: Boolean,
        default: false
      },
      stripeTerminalLocationIds: {
        type: JsonArray,
        excludeFromDefault: true,
        default: () => ([])
      },
      stripeAccountId: {
        type: String,
        excludeFromDefault: true,
        default: null
      },
      standardConnectAccountId: {
        type: String,
        default: null,
        output: true
      },
      stripePayoutBankAccounts: {
        type: JsonArray,
        default: () => ([]),
        excludeFromDefault: true
      },
      stripeReaderAutoPay: {
        type: Boolean,
        default: false
      },
      stripeRateHundredths: {
        type: Integer,
        excludeFromDefault: true
      },
      stripePresentPerTransactionCents: {
        type: Integer,
        excludeFromDefault: true
      },
      stripeNotPresentPerTransactionCents: {
        type: Integer,
        excludeFromDefault: true
      },
      indyCreditCardRateHundredths: {
        type: Integer,
        excludeFromDefault: true
      },
      indyCashRateHundredths: {
        type: Integer,
        excludeFromDefault: true
      },
      indyPerTransactionFeeCapCents: {
        type: Integer,
        excludeFromDefault: true
      },
      indyFixedDailyFeeCents: {
        type: Integer,
        excludeFromDefault: true
      },
      indyFixedServicesDailyFeeCents: {
        type: Integer,
        excludeFromDefault: true
      },
      stripeConnectManualPayoutsEnabled: {
        type: Boolean,
        default: true,
        excludeFromDefault: true
      },
      externalTicketingTransfersEnabled: {
        type: Boolean,
        default: true,
        excludeFromDefault: true
      },
      netlifySiteName: {
        type: String,
        excludeFromDefault: true,
        default: null
      },
      netlifySiteId: {
        type: String,
        excludeFromDefault: true,
        default: null
      },

      appStoreName: {
        type: String,
        excludeFromDefault: true,
        default: null
      },
      appStoreBundleIdSuffix: {
        type: String,
        excludeFromDefault: true,
        default: null
      },
      appVersion: {
        type: String,
        excludeFromDefault: true,
        default: null
      },

      gitBranch: {
        type: String,
        excludeFromDefault: true,
        default: null
      },

      hostname: {
        type: String,
        excludeFromDefault: true,
        default: null
      },
      friendlyHostname: {
        type: String,
        excludeFromDefault: true,
        default: null
      },
      isHostnameSharedAcrossSites: {
        type: Boolean,
        excludeFromDefault: true,
        output: true,
        default: false
      },
      sharedHostnameSiteIdsByPath: {
        type: JsonObject,
        excludeFromDefault: true,
        output: true,
        default: () => ({})
      },

      anonymousUserId: {
        type: String,
        excludeFromDefault: true,
        default: null
      },
      customerioTransactionTemplateId: {
        type: Integer,
        excludeFromDefault: true,
        default: null
      },
      customerioTransactionSmsTemplateId: {
        type: Integer,
        excludeFromDefault: true,
        default: null
      },
      customerioTransactionSmsWaitlistNotificationTemplateId: {
        type: Integer,
        excludeFromDefault: true,
        default: null
      },
      customerioTransactionEmailWaitlistNotificationTemplateId: {
        type: Integer,
        excludeFromDefault: true,
        default: null
      },
      customerioResetPasswordTemplateId: {
        type: Integer,
        excludeFromDefault: true,
        default: null
      },
      customerioVoucherPurchaseTemplateId: {
        type: Integer,
        excludeFromDefault: true,
        default: null
      },
      customerioGiftCardTemplateId: {
        type: Integer,
        excludeFromDefault: true,
        default: null
      },
      customerioConcessionsReminderTemplateId: {
        type: Integer,
        excludeFromDefault: true,
        default: null
      },
      customerioShowingChangeTemplateId: {
        type: Integer,
        excludeFromDefault: true,
        default: null
      },
      customerioDeliverOrderTemplateId: {
        type: Integer,
        excludeFromDefault: true,
        default: null
      },
      customerioOtpCodeTemplateId: {
        type: Integer,
        excludeFromDefault: true,
        default: null
      },
      customerioOtpSmsTemplateId: {
        type: Integer,
        excludeFromDefault: true,
        default: null
      },
      customerioDonationReceiptTemplateId: {
        type: Integer,
        excludeFromDefault: true,
        default: null
      },
      exchangeFeeEnabled: {
        type: Boolean,
        default: false
      },
      exchangeFeeType: {
        type: String,
        default: 'ticket',
        options: ['ticket', 'order']
      },
      exchangeFeeDefaultAmount: {
        type: Number,
        encode: encoders.cents,
        default: 0
      },
      exchangeFeeApplyToFreeTickets: {
        type: Boolean,
        default: false
      },
      exchangeFeeText: {
        type: String,
        default: 'Exchange Fee'
      },
      exchangeFeeTaxEnabled: {
        type: Boolean,
        default: false
      },
      exchangeFeeTaxes: {
        type: [Tax],
        default: () => ([]),
        reference: true
      },
      surchargeTaxEnabled: {
        type: Boolean,
        default: false
      },
      surchargeFeeTaxes: {
        type: [Tax],
        default: () => ([]),
        reference: true
      },
      showSavedCardPrimaryPaymentMethod: {
        type: Boolean,
        default: true
      },
      remoteAndCustomPaymentTypes: {
        type: JsonArray,
        default: () => ([]),
        output: true,
        excludeFromDefault: true
      },
      inMaintenanceWindow: {
        type: Boolean,
        default: false,
        excludeFromDefault: true,
        output: true
      },
      paymentConnectorTerminalOptions: {
        type: JsonArray,
        default: () => ([]),
        excludeFromDefault: true,
        output: true
      },
      futureBookingWeeksToAdd: {
        type: Number,
        default: 25
      },

      customReceiptHeaderText: {
        type: String,
        default: null,
        excludeFromDefault: true,
        translatable: true
      },
      customReceiptFooterText: {
        type: String,
        default: null,
        excludeFromDefault: true,
        translatable: true
      },

      action: {
        type: String,
        input: true,
        default: null
      },

      previewTime: {
        type: IsoDateTime,
        default: null,
        local: true
      },

      inventoryItemBaseUnits: {
        type: [String],
        excludeFromDefault: true,
        output: true
      },

      marketingPermissionsAutoOptIn: {
        type: Boolean,
        default: false
      },
      marketingPermissionsAutoSelect: {
        type: Boolean,
        default: false
      },
      inventoryStrategy: {
        type: String,
        default: INVENTORY_STRATEGY_TYPES.LOCATION,
        options: INVENTORY_STRATEGY_TYPES.ALL
      },
      activeInventoryStrategy: {
        type: InventoryStrategy,
        default: null,
        output: true
      },
      inventoryStrategies: {
        type: [InventoryStrategy],
        acceptsNestedAttributes: true,
        default () {
          return []
        }
      },
      giftCardsRequireAssignedCard: {
        type: Boolean,
        default: false
      },
      startDate: {
        type: IsoDate,
        excludeFromDefault: true,
        default: null,
        output: true
      },
      endDate: {
        type: IsoDate,
        excludeFromDefault: true,
        default: null,
        output: true
      },
      accountErrors: {
        type: JsonArray,
        excludeFromDefault: true,
        output: true
      },
      migratedTo: {
        type: String,
        output: true,
        default: null
      },
      deletedAt: {
        type: IsoDateTime,
        output: true,
        default: null
      },

      polarisEnabled: {
        type: Boolean,
        default: false,
        excludeFromDefault: true
      },
      polarisHourSend: {
        type: Number,
        default: 4,
        excludeFromDefault: true
      },
      polarisAuthenticationFields: {
        type: JsonObject,
        default: () => ({}),
        excludeFromDefault: true
      },
      polarisDataStreamIds: {
        type: JsonObject,
        default: () => ({}),
        excludeFromDefault: true
      },
      polarisBankingCategoryIds: {
        type: JsonObject,
        default: () => ({}),
        excludeFromDefault: true
      },
      automatedAccountingDayLockingEnabled: {
        type: Boolean,
        default: false
      },
      lrsPagerEnableValue: {
        type: Boolean,
        default: false,
        excludeFromDefault: true
      },
      lrsPagerIpAddressValue: {
        type: String,
        default: null,
        excludeFromDefault: true
      },
      lrsPagerSystemIdValue: {
        type: String,
        default: '0',
        excludeFromDefault: true
      },
      lrsPagerHardwareIdValue: {
        type: String,
        default: '0',
        excludeFromDefault: true
      },
      venueValetEnableValue: {
        type: Boolean,
        default: false,
        excludeFromDefault: true
      },
      venueValetUrlValue: {
        type: String,
        default: null,
        excludeFromDefault: true
      },
      dboxEnabledValue: {
        type: Boolean,
        default: false
      },
      hotschedulesEnabledValue: {
        type: Boolean,
        default: false
      },
      hotschedulesStoreNumberValue: {
        type: String,
        default: null,
        excludeFromDefault: true
      },
      hotschedulesConceptValue: {
        type: String,
        default: null,
        excludeFromDefault: true
      },
      hotschedulesCompanyIdValue: {
        type: String,
        default: null,
        excludeFromDefault: true
      },
      hotschedulesUsernameValue: {
        type: String,
        default: null,
        excludeFromDefault: true
      },
      hotschedulesPasswordValue: {
        type: String,
        default: null,
        excludeFromDefault: true
      },
      hotschedulesForecastDriverEnabledValue: {
        type: Boolean,
        default: false,
        excludeFromDefault: true
      },
      hotschedulesForecastDriverIntervalMinutesValue: {
        type: Integer,
        default: 60,
        excludeFromDefault: true
      },
      hotschedulesForecastDriverRvcIdValue: {
        type: Integer,
        default: null,
        excludeFromDefault: true
      }
    }

    this.computed = {
      active () {
        return !this.deletedAt
      },

      packageReferenceMap () {
        const packageToItemMap = new Map()
        const ticketTypeToPackagesMap = new Map()
        const itemVariationToPackagesMap = new Map()

        for (const itemPackage of this.configuration?.itemPackages?.data || []) {
          if (!itemPackage.applyAutomatically) continue
          const ticketTypeIds = []
          const itemVariationIds = []
          for (const packageItem of itemPackage.itemPackageItems.data) {
            if (packageItem.itemType === ORDER_ITEM_TYPES.TICKET) {
              ticketTypeIds.push(packageItem.ticketType.id)
              if (!ticketTypeToPackagesMap.has(packageItem.ticketType.id)) {
                ticketTypeToPackagesMap.set(packageItem.ticketType.id, new Set())
              }
              ticketTypeToPackagesMap.get(packageItem.ticketType.id).add(itemPackage.id)
            } else if (packageItem.itemType === ORDER_ITEM_TYPES.ITEM) {
              itemVariationIds.push(packageItem.itemVariation.id)
              if (!itemVariationToPackagesMap.has(packageItem.itemVariation.id)) {
                itemVariationToPackagesMap.set(packageItem.itemVariation.id, new Set())
              }
              itemVariationToPackagesMap.get(packageItem.itemVariation.id).add(itemPackage.id)
            }
          }

          packageToItemMap.set(itemPackage.id, {
            displayOrder: itemPackage.displayOrder,
            ticketTypeIds,
            itemVariationIds
          })
        }

        return {
          packageToItemMap,
          ticketTypeToPackagesMap,
          itemVariationToPackagesMap
        }
      },
      packageById () {
        const packageById = {}
        for (const itemPackage of this.configuration?.itemPackages?.data || []) {
          packageById[itemPackage.id] = itemPackage
        }
        return packageById
      },
      recaptchaRequired () {
        return isConsumer && !this.context?.isFeatureEnabled(this.$featureFlags.disable_google_recaptcha)
      },
      today () {
        return this.siteDate
      },
      tomorrow () {
        return new IsoDateTime(addToDate((this.previewTime || this.currentTime), { days: 1 })).inTimeZone(this.timeZone).format('YYYY-MM-DD')
      },
      businessTomorrow () {
        // 20 hours = 1 day - 4 hours
        return new IsoDateTime(addToDate(this.siteTime, { hours: 20 })).format('YYYY-MM-DD')
      },
      siteDate () {
        return this.siteTime.format('YYYY-MM-DD')
      },
      siteDayNumber () {
        return new IsoDateTime(addToDate(this.siteTime, { hours: -4 })).getDay()
      },
      businessDate () {
        return new IsoDateTime(addToDate(this.siteTime, { hours: -4 })).format('YYYY-MM-DD')
      },
      siteTimeWithSeconds () {
        return this.previewTime || this.currentTimeWithSeconds.inTimeZone(this.timeZone)
      },
      siteTime () {
        // Uncomment to control current time while developing
        // return new IsoDateTime(addToDate((this.previewTime || this.currentTime), { hours: -12 })).inTimeZone(this.timeZone)
        return this.previewTime || this.currentTime.inTimeZone(this.timeZone)
      },
      hasBookingFees () {
        return _.some(this.configuration.bookingFeeTypes.data, bookingFeeType => bookingFeeType.isActive && !!bookingFeeType.channels.website)
      },
      /** Always returns a path string starting with '/' without a trailing '/' */
      rootPath () {
        if (!this.hostname) {
          const root = prependWithConsumerRoot('/')
          return root === '/' ? '/' : _.trimEnd(root, '/')
        }

        let path
        try {
          const parsedUrl = new URL(this.hostname)
          path = parsedUrl.pathname
        } catch (e) {
          const root = prependWithConsumerRoot('/')
          return root === '/' ? '/' : _.trimEnd(root, '/')
        }

        const newPath = isConsumer ? prependWithConsumerRoot(path) : path
        return newPath === '/' ? '/' : _.trimEnd(newPath, '/')
      },
      /** Excludes the '/' site */
      sharedHostnamePathMatchersBySiteId () {
        const regexpBySiteId = {}
        _.each(this.sharedHostnameSiteIdsByPath, (siteId, sitePath) => {
          if (sitePath !== '/') {
            regexpBySiteId[siteId] = createConsumerSitePathRegexp(sitePath)
          }
        })
        return regexpBySiteId
      },
      primaryForImgix () {
        if (this.primaryColor) {
          return this.primaryColor.slice(1)
        } else {
          return 'FFFFFF'
        }
      },
      backgroundForImgix () {
        if (this.backgroundColor) {
          return this.backgroundColor.slice(1)
        } else {
          return 'FFFFFF'
        }
      },
      splashBackgroundForImgix () {
        if (this.splashBackgroundColor) {
          return this.splashBackgroundColor.slice(1)
        } else {
          return 'FFFFFF'
        }
      },
      darkLogoImageUrl () {
        if (!this.darkLogoImage) return ''

        return `${imgixHost}/${this.darkLogoImage}?${this.darkLogoImageParams}`
      },
      darkLogoImagePreviewUrl () {
        if (!this.darkLogoImage) return ''

        return `${imgixHost}/${this.darkLogoImage}?${this.darkLogoImagePreviewParams}`
      },

      lightLogoImageUrl () {
        if (!this.lightLogoImage) return ''

        return `${imgixHost}/${this.lightLogoImage}?${this.lightLogoImageParams}`
      },
      lightLogoImagePreviewUrl () {
        if (!this.lightLogoImage) return ''

        return `${imgixHost}/${this.lightLogoImage}?${this.lightLogoImagePreviewParams}`
      },

      darkLogoMarkImageUrl () {
        if (!this.darkLogoMarkImage) return ''

        return `${imgixHost}/${this.darkLogoMarkImage}?${this.darkLogoMarkImageParams}`
      },
      darkLogoMarkImagePreviewUrl () {
        if (!this.darkLogoMarkImage) return ''

        return `${imgixHost}/${this.darkLogoMarkImage}?${this.darkLogoMarkImagePreviewParams}`
      },

      lightLogoMarkImageUrl () {
        if (!this.lightLogoMarkImage) return ''

        return `${imgixHost}/${this.lightLogoMarkImage}?${this.lightLogoMarkImageParams}`
      },
      lightLogoMarkImagePreviewUrl () {
        if (!this.lightLogoImage) return ''

        return `${imgixHost}/${this.lightLogoMarkImage}?${this.lightLogoMarkImagePreviewParams}`
      },
      siteThumbnailImageUrl () {
        if (!this.consumerConfig.siteThumbnailImage) return ''

        return `${imgixHost}/${this.consumerConfig.siteThumbnailImage}?w=1200`
      },
      siteThumbnailImagePreviewUrl () {
        if (!this.consumerConfig.siteThumbnailImage) return ''

        return `${imgixHost}/${this.consumerConfig.siteThumbnailImage}?w=400&transparency=grid`
      },
      iconImageUrl () {
        if (!this.iconImage) return ''

        return `${imgixHost}/${this.iconImage}?${this.iconImageParams}`
      },
      iconImagePreviewUrl () {
        if (!this.iconImage) return ''

        return `${imgixHost}/${this.iconImage}?${this.iconImagePreviewParams}`
      },
      iconImageAppleTouchPreviewUrl () {
        if (!this.iconImage) return ''

        return `${imgixHost}/${this.lightLogoMarkImage}?fm=png&w=128&h=128&fit=fill&fill=solid&pad=9&fill-color=${this.primaryForImgix}&bg=${this.primaryForImgix}&auto=format,compress`
      },

      faviconImageUrl () {
        if (!this.faviconImage) return ''

        return `${imgixHost}/${this.faviconImage}?${this.faviconImageParams}`
      },
      faviconImagePreviewUrl () {
        if (!this.faviconImage) return ''

        return `${imgixHost}/${this.faviconImage}?${this.faviconImagePreviewParams}`
      },

      splashImageUrl () {
        if (!this.splashImage) return ''

        return `${imgixHost}/${this.splashImage}?${this.splashImageParams}`
      },
      splashImagePreviewUrl () {
        if (!this.splashImage) return ''

        return `${imgixHost}/${this.splashImage}?${this.splashImagePreviewParams}`
      },
      splashImageExampleIPhoneUrl () {
        if (!this.splashImage) return ''

        return `${imgixHost}/${this.splashImage}?fm=png&w=640&h=1136&fit=fillmax&fill=solid&pad=60&fill-color=${this.splashBackgroundForImgix}&bg=${this.splashBackgroundForImgix}&auto=format,compress`
      },
      splashImageExampleIPadUrl () {
        if (!this.splashImage) return ''

        return `${imgixHost}/${this.splashImage}?fm=png&w=1536&h=2048&fit=fillmax&fill=solid&pad=60&fill-color=${this.splashBackgroundForImgix}&bg=${this.splashBackgroundForImgix}&auto=format,compress`
      },
      splashImageExampleDesktopUrl () {
        if (!this.splashImage) return ''

        return `${imgixHost}/${this.splashImage}?fm=png&w=2000&h=1500&fit=fillmax&fill=solid&pad=60&fill-color=${this.splashBackgroundForImgix}&bg=${this.splashBackgroundForImgix}&auto=format,compress`
      },

      receiptLogoImageUrl () {
        if (!this.receiptLogoImage) return ''

        return `${imgixHost}/${this.receiptLogoImage}?${this.receiptLogoImageParams}`
      },
      appleStoreImageUrl () {
        if (!this.appleStoreImage) return ''

        return `${imgixHost}/${this.appleStoreImage}?${this.appleStoreImageParams}`
      },
      rewardCurrencyImageUrl () {
        if (!this.rewardCurrencyImage) return ''

        return `${imgixHost}/${this.rewardCurrencyImage}?${this.rewardCurrencyImageParams}`
      },
      discountImageUrl () {
        if (!this.discountImage) return ''

        return `${imgixHost}/${this.discountImage}?${this.registerButtonImageParams}`
      },
      voucherSaleImageUrl () {
        if (!this.voucherSaleImage) return ''

        return `${imgixHost}/${this.voucherSaleImage}?${this.registerButtonImageParams}`
      },
      voucherServiceIssueImageUrl () {
        if (!this.voucherServiceIssueImage) return ''

        return `${imgixHost}/${this.voucherServiceIssueImage}?${this.registerButtonImageParams}`
      },
      giftCardImageUrl () {
        if (!this.giftCardImage) return ''

        return `${imgixHost}/${this.giftCardImage}?${this.registerButtonImageParams}`
      },
      miscItemImageUrl () {
        if (!this.miscItemImage) return ''

        return `${imgixHost}/${this.miscItemImage}?${this.registerButtonImageParams}`
      },
      cashOutItemImageUrl () {
        if (!this.cashOutItemImage) return ''

        return `${imgixHost}/${this.cashOutItemImage}?${this.registerButtonImageParams}`
      },
      donationItemImageUrl () {
        if (!this.donationItemImage) return ''

        return `${imgixHost}/${this.donationItemImage}?${this.registerButtonImageParams}`
      },
      userCreditImageUrl () {
        if (!this.userCreditImage) return ''

        return `${imgixHost}/${this.userCreditImage}?${this.registerButtonImageParams}`
      },
      applyVoucherImageUrl () {
        if (!this.applyVoucherImage) return ''

        return `${imgixHost}/${this.applyVoucherImage}?${this.registerButtonImageParams}`
      },
      onlineDeliveryTypeOptions () {
        return _.concat(this.arrayToOptions(_.compact([
          ((this.counterPickupEnabled && this.counterPickupAvailableOnline) ? 'counter-pickup' : null),
          ((this.expressPickupEnabled && this.expressPickupAvailableOnline) ? 'express-pickup' : null),
          ((this.theaterSeatDeliveryEnabled && this.theaterSeatDeliveryAvailableOnline) ? 'theater-seat' : null),
          ((this.tableDeliveryEnabled && this.tableDeliveryAvailableOnline) ? 'table' : null)
        ])), _.map(
          _.filter(this.configuration?.deliveryTypes?.data, deliveryType => {
            return deliveryType.enable && deliveryType.availableOnline
          }),
          deliveryType => {
            return { label: deliveryType.name, value: deliveryType.id.toString() }
          }))
      },
      siteDeliveryTypeOptions () {
        return _.compact([
          (this.counterPickupEnabled ? 'counter-pickup' : null),
          (this.expressPickupEnabled ? 'express-pickup' : null),
          (this.theaterSeatDeliveryEnabled ? 'theater-seat' : null),
          (this.tableDeliveryEnabled ? 'table' : null)
        ])
      },
      counterPickupIdentifierOptions () {
        return this.arrayToOptions(_.compact([
          COUNTER_PICKUP_IDENTIFIER_OPTIONS.USER_NAME,
          ((this.theaterSeatDeliveryEnabled && this.theaterSeatDeliveryAvailableOnline) ? COUNTER_PICKUP_IDENTIFIER_OPTIONS.SEAT_NAME : null),
          ((this.anonymousOrderRequirements.consumer_prompt_for_name) ? COUNTER_PICKUP_IDENTIFIER_OPTIONS.DELIVERY_NAME : null),
          COUNTER_PICKUP_IDENTIFIER_OPTIONS.CUSTOM
        ]))
      },
      itemPriceLabel () {
        return `${this.concessionPricesIncludeTax ? 'Gross' : 'Net'} Price`
      },
      ticketPriceLabel () {
        return `${this.ticketPricesIncludeTax ? 'Gross' : 'Net'} Price`
      },
      itemAmountLabel () {
        return `${this.concessionPricesIncludeTax ? 'Gross' : 'Net'} Amount`
      },
      ticketAmountLabel () {
        return `${this.ticketPricesIncludeTax ? 'Gross' : 'Net'} Amount`
      },
      addressShort () {
        if (this.state?.length) return this.city + ', ' + this.state
        return this.city
      },
      addressFull () {
        const phone = this.phone?.length && !this.consumerConfig.hidePhone ? `\n${this.phone}` : ''
        const email = this.email?.length && !this.consumerConfig.hideEmail ? `\n${this.email}` : ''
        return `${this.address1} ${this.city} ${this.state} ${this.zip}.` + phone + email
      },
      itemReportingSuffix () {
        return this.concessionPricesIncludeTax ? 'Gross' : 'Net'
      },
      ticketTypeReportingSuffix () {
        return this.ticketPricesIncludeTax ? 'Gross' : 'Net'
      },
      displayName () {
        return this.consumerConfig.displayName || this.name
      },
      label () {
        return this.name
      },
      value () {
        return this.id
      }
    }

    this.queries = {
      currentSite: {
        results: {
          site: Site
        }
      },
      allSites: {
        results: {
          sites: [Site]
        }
      },
      runnableIntegrationActions: {
        results: {
          value: Generic
        }
      }
    }

    this.mutations = {
      migrateToStandardConnect: {
        arguments: {
          standardConnectAccountId: {
            type: String,
            required: true
          }
        },
        results: {
          success: Boolean,
          errors: [GqlError]
        }
      },
      setAllowItemOrdersOnline: {
        arguments: {
          id: {
            type: ID,
            required: true
          },
          value: {
            type: Boolean,
            required: true
          }
        },
        results: {
          success: Boolean
        }
      },
      createStripeReader: {
        arguments: {
          registrationCode: {
            type: String,
            required: true
          },
          label: {
            type: String,
            required: true
          }
        },
        results: {
          success: Boolean
        }
      },
      copyMovieDisplayOrder: {
        results: {
          success: Boolean,
          errors: [GqlError]
        }
      },
      runIntegrationAction: {
        arguments: {
          action: {
            type: String,
            required: true
          },
          args: {
            type: JsonObject,
            required: false
          }
        },
        results: {
          actionResult: Generic
        }
      }
    }
  }

  // Page URL methods

  url (subPath = '') {
    if (!subPath) return this.hostname

    const hostnameWithoutPath = deleteSuffix(this.hostname || '', this.rootPath)
    return `${hostnameWithoutPath}${this.path(subPath)}`
  }

  path (subPath = '') {
    if (!subPath) return this.rootPath

    if (consumerRoot !== '/') {
      // Normalize to path without any consumerRoot prefix for comparison
      // and because it is re-prefixed by rootPath below.
      subPath = deletePrefix(subPath, consumerRoot) || '/'
      subPath = `/${_.trim(subPath, '/')}`
    }

    if (subPath === this.rootPath) {
      return subPath
    } else if (this.rootPath !== '/' && _.startsWith(subPath, `${this.rootPath}/`)) {
      return subPath
    }

    subPath = _.trim(subPath, '/')
    return this.rootPath === '/' ? `/${subPath}` : `${this.rootPath}/${subPath}`
  }

  get displayName () {
    return this.siteTitle || this.name
  }

  get hasOnlineDelivery () {
    return (this.counterPickupEnabled && this.counterPickupAvailableOnline) ||
           (this.expressPickupEnabled && this.expressPickupAvailableOnline) ||
           (this.theaterSeatDeliveryEnabled && this.theaterSeatDeliveryAvailableOnline) ||
           _.some(this.configuration?.deliveryTypes?.data, deliveryType => deliveryType.enable && deliveryType.availableOnline)
  }

  get allowItemSalesWithTicketPurchases () {
    return this.consumerConfig.allowItemSalesWithTicketPurchases
  }

  get enableSmsReceiptsForQrCodeOrders () {
    return this.configuration.currentCircuit.enableSms && this.consumerConfig.enableSmsReceiptsForQrCodeOrders
  }

  set enableSmsReceiptsForQrCodeOrders (value) {
    this.consumerConfig.enableSmsReceiptsForQrCodeOrders = value
  }

  get allowQrCodeDeliveryLocationPagerRequests () {
    return ((this.tableDeliveryEnabled && this.tableDeliveryAvailableOnline) ||
           (this.theaterSeatDeliveryEnabled && this.theaterSeatDeliveryAvailableOnline)) &&
           this.checkoutOptions.allow_qr_code_delivery_location_pager_requests
  }

  set allowQrCodeDeliveryLocationPagerRequests (value) {
    this.checkoutOptions.allow_qr_code_delivery_location_pager_requests = value
  }

  get themeColorsCss () {
    return `
      :root {
        --q-primary: ${this.primaryColor};
        --q-secondary: ${this.secondaryColor};
        --q-accent: ${this.accentColor};
        --q-positive: ${this.positiveColor};
        --q-negative: ${this.negativeColor};
        --q-info: ${this.infoColor};
        --q-warning: ${this.warningColor};
        --q-dark: ${this.darkColor};
      }`
  }

  defaultPriceCard (dayNumber, timeSlotId) {
    const priceCardId = this.defaultPriceCards[timeSlotKey(dayNumber, timeSlotId)]

    return this.configuration.priceCards.get(priceCardId)
  }

  navigateToLogoRoute (router) {
    let url = this.logoRoute

    if (_.startsWith(url, 'http')) {
      window.location = url
    } else {
      // Routing within our site
      if (_.startsWith(url, '/#/')) url = url.substr(2)

      if (_.startsWith(url, '#/')) url = url.substr(1)

      router.push(url)
    }
  }

  async triggerSiteDeploy () {
    this.action = 'trigger_netlify_deploy'

    let result
    try {
      result = await this.save()
      if (result.errors.length) {
        console.log(result.errors)
        this.$mh.showErrors(result.errors)
      }
    } catch (error) {
      console.log(error)
      return false
    } finally {
      this.resetField('action')
    }

    return result.errors.length === 0
  }

  async setAllowItemOrdersOnline () {
    const results = await executeMutation('setAllowItemOrdersOnline', { id: this.id, value: this.allowItemOrdersOnline }, { success: true })

    return results
  }

  async createStripeReader (registrationCode, label) {
    const results = await executeMutation('createStripeReader', { registrationCode, label }, { success: true })

    return results
  }

  async runIntegrationAction (action, args) {
    const results = await executeMutation('runIntegrationAction', { action, args }, {
      actionResult: {
        structure: {
          value: true
        }
      }
    })

    return results
  }

  async copyMovieDisplayOrderFromPreviousWeek () {
    const results = await executeMutation(
      'copyMovieDisplayOrder',
      {},
      {
        success: true,
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )

    return results
  }

  async migrateToStandardConnect (standardConnectAccountId) {
    const results = await executeMutation(
      'migrateToStandardConnect',
      { standardConnectAccountId },
      {
        success: true,
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )

    return results
  }

  inSiteTime (time) {
    if (!time) return time

    if (time.inTimeZone) return time.inTimeZone(this.timeZone)

    return new IsoDateTime(time).inTimeZone(this.timeZone)
  }

  siteEffectiveDateForTime (time) {
    if (!time) return time

    if (!time.inTimeZone) time = new IsoDateTime(time)

    return this.inSiteTime(addToDate(time, { hours: -DAY_CUTOFF_HOUR_OFFSET })).toIsoDate()
  }

  getOffsetMinutes (timeZone = 'UTC', date = new Date()) {
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }))
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone }))
    return (tzDate.getTime() - utcDate.getTime()) / 60 / 1000
  }

  inGMT (time) {
    if (!time) return time

    const timeZoneOffset = this.getOffsetMinutes(this.timeZone, time) - this.getOffsetMinutes(Intl.DateTimeFormat().resolvedOptions().timeZone, this.time)
    return new IsoDateTime(date.addToDate(time, { minutes: -timeZoneOffset }))
  }
}
